import React, { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { RotateCcw, X, Loader2 } from 'lucide-react';
import { chronologyApi } from '../../services/chronologyApi';
import { formatDate } from '../../utils/dateFormat';

interface OriginalContentPopoverProps {
  event: {
    eventId: string;
    title?: string;
    summary?: string;
    description: string;
    timestamp: string | { start: string; end: string };
    type: string;
    originalHeadline?: string;
    originalSummary?: string;
    originalDescription?: string;
    originalEventDate?: string;
    originalEventType?: string;
  };
  caseId: string;
  onClose: () => void;
  onReverted: () => void;
}

export const OriginalContentPopover: React.FC<OriginalContentPopoverProps> = ({
  event,
  caseId,
  onClose,
  onReverted,
}) => {
  const [confirmRevert, setConfirmRevert] = useState(false);
  const queryClient = useQueryClient();

  const revertMutation = useMutation(
    () => chronologyApi.revertEvent(event.eventId),
    {
      onSuccess: async () => {
        await queryClient.refetchQueries(['timeline', caseId]);
        onReverted();
      },
    }
  );

  const hasOriginal =
    event.originalHeadline != null ||
    event.originalSummary != null ||
    event.originalDescription != null ||
    event.originalEventDate != null ||
    event.originalEventType != null;

  if (!hasOriginal) {
    return (
      <div
        className="mt-3 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-950/20 p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold text-amber-700 dark:text-amber-300 uppercase tracking-wide">
            Original Content
          </span>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
        <p className="text-xs text-medical-500 dark:text-medical-400">
          Original content not available for this event (edited before tracking was added).
        </p>
      </div>
    );
  }

  const currentTimestamp =
    typeof event.timestamp === 'string'
      ? event.timestamp
      : event.timestamp.start;

  const fields = [
    {
      label: 'Headline',
      current: event.title || '',
      original: event.originalHeadline,
    },
    {
      label: 'Summary',
      current: event.summary || '',
      original: event.originalSummary,
    },
    {
      label: 'Description',
      current: event.description,
      original: event.originalDescription,
    },
    {
      label: 'Date',
      current: formatDate(currentTimestamp),
      original: event.originalEventDate
        ? formatDate(event.originalEventDate)
        : undefined,
    },
    {
      label: 'Type',
      current: event.type,
      original: event.originalEventType?.toLowerCase(),
    },
  ].filter(
    (f) => f.original != null && f.original !== f.current
  );

  return (
    <div
      className="mt-3 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-950/20 p-4"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-semibold text-amber-700 dark:text-amber-300 uppercase tracking-wide">
          Original AI-Extracted Content
        </span>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {fields.length === 0 ? (
        <p className="text-xs text-medical-500 dark:text-medical-400">
          All fields match the original extraction.
        </p>
      ) : (
        <div className="space-y-2">
          {fields.map((f) => (
            <div key={f.label} className="text-xs">
              <span className="font-medium text-medical-600 dark:text-medical-400">
                {f.label}:
              </span>
              <div className="mt-0.5 ml-2 space-y-1">
                <div className="flex items-start gap-1.5">
                  <span className="text-red-500 dark:text-red-400 font-medium shrink-0">Current:</span>
                  <span className="text-medical-700 dark:text-medical-300 line-through opacity-70">
                    {f.current || '(empty)'}
                  </span>
                </div>
                <div className="flex items-start gap-1.5">
                  <span className="text-green-600 dark:text-green-400 font-medium shrink-0">Original:</span>
                  <span className="text-medical-800 dark:text-medical-200">
                    {f.original}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-3 pt-3 border-t border-amber-200 dark:border-amber-700 flex items-center gap-2">
        {!confirmRevert ? (
          <button
            onClick={() => setConfirmRevert(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-amber-100 text-amber-700 hover:bg-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:hover:bg-amber-900/50 transition-colors"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            Revert to Original
          </button>
        ) : (
          <>
            <span className="text-xs text-red-600 dark:text-red-400">
              This will discard your edits. Continue?
            </span>
            <button
              onClick={() => revertMutation.mutate()}
              disabled={revertMutation.isLoading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400 transition-colors disabled:opacity-50"
            >
              {revertMutation.isLoading ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : null}
              Yes, Revert
            </button>
            <button
              onClick={() => setConfirmRevert(false)}
              className="text-xs text-medical-500 hover:text-medical-700 dark:hover:text-medical-300"
            >
              Cancel
            </button>
          </>
        )}
      </div>
    </div>
  );
};
